import type { App, Plugin } from 'vue';
import {
  Authenticator,
  BasicAuthenticator,
  CognitoAuthenticator,
  EntraAuthenticator,
  AzureB2CAuthenticator,
  configureEntra,
  configureAzureB2C,
  createNavigationGuard,
  GetUserInjectionKey,
  setCurrentAuthenticator
} from '@infuse/auth/services';
import { LoginLayout } from '@infuse/ui/layouts';
import {
  ForgotPasswordPage,
  LoginPage,
  NotAllowedPage,
  ResetPasswordPage,
  UserDeactivatedPage
} from '@infuse/auth/pages';
import { AppRouter } from '@infuse/ui/router';

export type AuthType = 'cognito' | 'basic' | 'entra' | 'azureB2c';

export interface EntraOptions {
  clientId: string;
  tenantId: string;
  redirectUri: string;
  logoutRedirectUri?: string;
  scopes: string[];
}

export interface AzureB2COptions {
  clientId: string;
  tenantName: string;
  policies: string[];
  redirectUri: string;
  logoutRedirectUri?: string;
  scopes?: string[];
}

export interface AuthPluginOptions {
  type: AuthType;
  entra?: EntraOptions;
  azureB2C?: AzureB2COptions;
}

export function createAuth(options: AuthPluginOptions, callback: (authenticator: Authenticator) => void): Plugin {
  return {
    install(app: App) {
      let authenticator: Authenticator;

      switch (options.type) {
        case 'cognito':
          authenticator = new CognitoAuthenticator();
          break;
        case 'basic':
          authenticator = new BasicAuthenticator();
          break;
        case 'entra':
          // Prefer using a previously configured global Entra config, but if
          // options are provided here use them directly for this instance.
          if (options.entra) {
            configureEntra({
              clientId: options.entra.clientId,
              tenantId: options.entra.tenantId,
              redirectUri: options.entra.redirectUri,
              logoutRedirectUri: options.entra.logoutRedirectUri,
              scopes: options.entra.scopes
            });
          }
          authenticator = new EntraAuthenticator();
          break;
        case 'azureB2c':
          // Prefer using a previously configured global Azure B2C config, but if
          // options are provided here use them directly for this instance.
          if (options.azureB2C) {
            configureAzureB2C({
              clientId: options.azureB2C.clientId,
              tenantName: options.azureB2C.tenantName,
              policies: options.azureB2C.policies,
              redirectUri: options.azureB2C.redirectUri,
              logoutRedirectUri: options.azureB2C.logoutRedirectUri,
              scopes: options.azureB2C.scopes || []
            });
          }
          authenticator = new AzureB2CAuthenticator();
          break;
        default:
          throw new Error(`Unsupported authentication type ${options.type}`);
      }

      setCurrentAuthenticator(authenticator);

      app.provide(GetUserInjectionKey, () => authenticator.getUser());

      callback(authenticator);

      AppRouter.addRoute({
        path: '/auth',
        name: 'auth',
        component: LoginLayout,
        children: [
          {
            name: 'auth-login',
            path: 'login',
            component: LoginPage
          },
          {
            name: 'auth-forgot-password',
            path: 'forgot-password',
            component: ForgotPasswordPage
          },
          {
            name: 'auth-reset-password',
            path: 'reset-password',
            component: ResetPasswordPage
          }
        ]
      });

      AppRouter.addRoute({
        name: 'application-not-allowed',
        path: '/application-not-allowed',
        component: NotAllowedPage,
        meta: {
          showLogout: true
        }
      });

      AppRouter.addRoute({
        name: 'not-allowed',
        path: '/not-allowed',
        component: NotAllowedPage
      });

      AppRouter.addRoute({
        name: 'user-deactivated',
        path: '/user-deactivated',
        component: UserDeactivatedPage,
        meta: {
          showLogout: true
        }
      });

      AppRouter.beforeEach(createNavigationGuard(authenticator));
    }
  };
}
